import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { Button } from '../../../web/components-v2/Button';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { ScenarioPicker } from '../spar/ScenarioPicker';
import type { Category, ScenarioWithCompletion, SkillArea } from '../spar/types';

export type PracticeModePanelProps = {
	/** Whether the practice-lead queue mode is currently active. */
	isActive: boolean;
	/** Enter practice-queue mode (serve real leads non-consuming). */
	onEnter: () => void;
	/** Leave practice-queue mode and return to the standard queue. */
	onExit: () => void;
	/** Practice scenarios available to the rep. */
	scenarios: ScenarioWithCompletion[];
	/** Skill areas — map scenarios up to their category for the picker grouping. */
	skillAreas: SkillArea[];
	/** Categories — the level the scenario picker groups by. */
	categories: Category[];
	/** Currently selected scenario id, or `null` for a random scenario. */
	selectedScenarioId: string | null;
	/** Fires with the chosen scenario id, or `null` for "Random scenario". */
	onSelectScenario: (id: string | null) => void;
	/** When `true`, the next practice lead is loading; the queue toggle is disabled. */
	isLoadingLead?: boolean;
	/**
	 * The contact a Spar would run against — the contact whose Spar button was
	 * clicked, or the first contact on the current lead. `null` when there's no
	 * lead/contact in view. Drives the "Start practicing" CTA.
	 */
	sparTarget: { name: string } | null;
	/** Start an ad-hoc Spar against {@link sparTarget} with the selected scenario. */
	onStartSpar: () => void;
	/** When `true`, a Spar is connecting; the Start button is disabled. */
	isSparConnecting?: boolean;
};

/**
 * Pure-presentation surface for practice mode in the focused-view queue.
 *
 * Mirrors the structure of `settings/SettingsPanel` — all state and
 * data-fetching live in the parent (`FocusedLeadView`). Two flows share the
 * scenario picker:
 *  - **Practice with a contact** (the primary CTA): pick a scenario, then
 *    "Start practicing" launches an AI voice roleplay ("Spar") against the
 *    target contact. This is where the per-contact Spar button lands.
 *  - **Practice queue**: toggle the queue into practice mode, where it keeps
 *    serving REAL leads non-consuming (`mode=PracticeCall`) so a rep can work a
 *    stream of leads without burning them. No scoring UI — scoring is out of
 *    scope.
 */
export const PracticeModePanel: FC<PracticeModePanelProps> = ({
	isActive,
	onEnter,
	onExit,
	scenarios,
	skillAreas,
	categories,
	selectedScenarioId,
	onSelectScenario,
	isLoadingLead = false,
	sparTarget,
	onStartSpar,
	isSparConnecting = false,
}) => {
	return (
		<div className={css(styles.root)}>
			<div className={css(styles.section)}>
				<h3 className={css(styles.title)}>Practice Mode</h3>
				<p className={css(styles.description)}>
					Rehearse a call against an AI that roleplays the prospect. Pick a scenario, then start practicing — no real
					prospect is contacted.
				</p>
			</div>

			<div className={css(styles.section)}>
				<label className={css(styles.label)}>Scenario</label>
				<ScenarioPicker
					scenarios={scenarios}
					skillAreas={skillAreas}
					categories={categories}
					selectedScenarioId={selectedScenarioId}
					onSelect={onSelectScenario}
				/>
			</div>

			<div className={css(styles.section)}>
				{sparTarget ? (
					<p className={css(styles.description)}>
						Practice with <strong className={css(styles.target)}>{sparTarget.name}</strong>
					</p>
				) : (
					<p className={css(styles.description)}>Click a contact’s Practice button to rehearse against them.</p>
				)}
				<Button
					variant='primary'
					styles={[styles.fullWidthButton]}
					disabled={!sparTarget || isSparConnecting}
					onClick={onStartSpar}
				>
					{isSparConnecting ? 'Starting…' : 'Start practicing'}
				</Button>
			</div>

			<div className={css(styles.section)}>
				<p className={css(styles.queueNote)}>
					Practice queue mode serves you real leads without removing them from your queue, so you can work through
					several without burning them.
				</p>
				<Button
					variant={isActive ? 'destructive-secondary' : 'secondary'}
					styles={[styles.fullWidthButton]}
					disabled={isLoadingLead}
					onClick={isActive ? onExit : onEnter}
				>
					{isActive ? 'Exit practice queue' : 'Enter practice queue'}
				</Button>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		padding: THEME.spacing.lg,
		height: '100%',
		overflowY: 'auto',
		// Use the SideDrawer's CSS-variable theming (like SettingsPanel and the
		// other drawer tabs) so this panel reads as the same elevation surface.
		background: 'var(--bg-elev)',
		color: 'var(--ink)',
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	title: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		color: 'var(--ink)',
	},
	label: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: 'var(--ink)',
	},
	description: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.xs,
		color: 'var(--ink-muted)',
	},
	target: {
		color: 'var(--ink)',
		fontWeight: THEME.typography.fontWeights.bold,
	},
	queueNote: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.xs,
		color: 'var(--ink-muted)',
	},
	fullWidthButton: {
		width: '100%',
	},
});
